import { ParsedInquiry } from '../types/ticket';

export interface GoogleSheetsConfig {
  spreadsheetId: string;
  apiKey: string;
  range: string;
}

export interface SyncResult {
  totalRows: number;
  newRows: number;
  processedRows: number;
  errors: string[];
}

export class GoogleSheetsService {
  private static readonly SPREADSHEET_ID = '1CU0upz2OJrtD1aHxW8BB6F5DPPTKzYy8tdzM4_X9I9w';
  private static readonly SHEETS_API_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';
  private static readonly RANGE = 'Sheet1!A:M'; // Columns A through M
  private static apiKey: string | null = null;

  /**
   * Initialize the service with API key
   */
  static initialize(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Get API key from environment or throw error
   */
  private static getApiKey(): string {
    if (this.apiKey) {
      return this.apiKey;
    }
    
    const envKey = import.meta.env.VITE_GOOGLE_SHEETS_API_KEY;
    if (envKey) {
      return envKey;
    }
    
    throw new Error('Google Sheets API key not configured. Please set VITE_GOOGLE_SHEETS_API_KEY environment variable or call initialize() method.');
  }

  /**
   * Test connection to Google Sheets
   */
  static async testConnection(): Promise<boolean> {
    try {
      const apiKey = this.getApiKey();
      const url = `${this.SHEETS_API_BASE}/${this.SPREADSHEET_ID}/values/${this.RANGE}?key=${apiKey}&majorDimension=ROWS&valueRenderOption=UNFORMATTED_VALUE`;
      
      const response = await fetch(url);
      return response.ok;
    } catch (error) {
      console.error('Google Sheets connection test failed:', error);
      return false;
    }
  }

  /**
   * Fetch all rows from the Google Sheet
   */
  static async fetchAllRows(): Promise<ParsedInquiry[]> {
    try {
      const apiKey = this.getApiKey();
      const url = `${this.SHEETS_API_BASE}/${this.SPREADSHEET_ID}/values/${this.RANGE}?key=${apiKey}&majorDimension=ROWS&valueRenderOption=UNFORMATTED_VALUE`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Google Sheets API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      const rows = data.values || [];
      
      if (rows.length === 0) {
        return [];
      }
      
      // Skip header row and convert to ParsedInquiry objects
      const dataRows = rows.slice(1);
      const inquiries: ParsedInquiry[] = [];
      
      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowIndex = i + 2; // +2 because we skip header and arrays are 0-indexed
        
        try {
          const inquiry = this.parseRowToInquiry(row, rowIndex);
          if (inquiry) {
            inquiries.push(inquiry);
          }
        } catch (error) {
          console.error(`Error parsing row ${rowIndex}:`, error);
        }
      }
      
      return inquiries;
    } catch (error) {
      console.error('Error fetching Google Sheets data:', error);
      throw error;
    }
  }

  /**
   * Fetch new rows since last sync
   */
  static async fetchNewRows(lastSyncedRowIndex: number): Promise<ParsedInquiry[]> {
    try {
      const allRows = await this.fetchAllRows();
      
      // Return only rows that are newer than the last synced row
      return allRows.filter(row => row.sheetRowIndex > lastSyncedRowIndex);
    } catch (error) {
      console.error('Error fetching new rows:', error);
      throw error;
    }
  }

  /**
   * Parse a spreadsheet row into a ParsedInquiry object
   */
  private static parseRowToInquiry(row: any[], rowIndex: number): ParsedInquiry | null {
    // Skip empty rows
    if (!row || row.length === 0 || !row[0]) {
      return null;
    }

    // Map columns to their respective fields
    const inquiry: ParsedInquiry = {
      date: row[0] || '',                    // Column A: Date
      from: row[1] || '',                    // Column B: From
      subject: row[2] || '',                 // Column C: Subject
      message: row[3] || '',                 // Column D: Message
      orderNumber: row[4] || '',             // Column E: Order #
      inquiryType: row[5] || '',             // Column F: Inquiry Type
      skuItem: row[6] || '',                 // Column G: SKU / Item
      itemTitle: row[7] || '',               // Column H: Item
      logAction: row[8] || '',               // Column I: Log
      customerName: row[9] || '',            // Column J: Customer Name
      customerEmail: row[10] || '',          // Column K: Customer Email
      location: row[11] || '',               // Column L: Location
      offerAmount: row[12] || '',            // Column M: Offer Amount
      sheetRowIndex: rowIndex
    };

    return inquiry;
  }

  /**
   * Get preview of sheet data (first 10 rows)
   */
  static async getPreview(): Promise<{
    headers: string[];
    sampleRows: ParsedInquiry[];
    totalEstimatedRows: number;
  }> {
    try {
      const apiKey = this.getApiKey();
      const previewRange = 'Sheet1!A1:M11'; // Header + 10 data rows
      const url = `${this.SHEETS_API_BASE}/${this.SPREADSHEET_ID}/values/${previewRange}?key=${apiKey}&majorDimension=ROWS&valueRenderOption=UNFORMATTED_VALUE`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Google Sheets API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      const rows = data.values || [];
      
      if (rows.length === 0) {
        return {
          headers: [],
          sampleRows: [],
          totalEstimatedRows: 0
        };
      }
      
      const headers = rows[0] || [];
      const dataRows = rows.slice(1);
      const sampleRows: ParsedInquiry[] = [];
      
      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowIndex = i + 2;
        
        try {
          const inquiry = this.parseRowToInquiry(row, rowIndex);
          if (inquiry) {
            sampleRows.push(inquiry);
          }
        } catch (error) {
          console.error(`Error parsing preview row ${rowIndex}:`, error);
        }
      }
      
      // Get total row count
      const totalRows = await this.getTotalRowCount();
      
      return {
        headers,
        sampleRows,
        totalEstimatedRows: Math.max(0, totalRows - 1) // Subtract header row
      };
    } catch (error) {
      console.error('Error getting preview:', error);
      throw error;
    }
  }

  /**
   * Get total row count in the sheet
   */
  private static async getTotalRowCount(): Promise<number> {
    try {
      const apiKey = this.getApiKey();
      const url = `${this.SHEETS_API_BASE}/${this.SPREADSHEET_ID}/values/Sheet1!A:A?key=${apiKey}&majorDimension=ROWS&valueRenderOption=UNFORMATTED_VALUE`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        return 0;
      }
      
      const data = await response.json();
      return data.values ? data.values.length : 0;
    } catch (error) {
      console.error('Error getting total row count:', error);
      return 0;
    }
  }

  /**
   * Validate that the expected columns exist in the sheet
   */
  static async validateSheetStructure(): Promise<{
    isValid: boolean;
    errors: string[];
    headers: string[];
  }> {
    try {
      const apiKey = this.getApiKey();
      const url = `${this.SHEETS_API_BASE}/${this.SPREADSHEET_ID}/values/Sheet1!A1:M1?key=${apiKey}&majorDimension=ROWS&valueRenderOption=UNFORMATTED_VALUE`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        return {
          isValid: false,
          errors: [`API Error: ${response.status} ${response.statusText}`],
          headers: []
        };
      }
      
      const data = await response.json();
      const headers = data.values?.[0] || [];
      
      const expectedHeaders = [
        'Date', 'From', 'Subject', 'Message', 'Order #', 
        'Inquiry Type', 'SKU / Item', 'Item', 'Log', 
        'Customer Name', 'Customer Email', 'Location', 'Offer Amount'
      ];
      
      const errors: string[] = [];
      
      // Check if we have the minimum required columns
      if (headers.length < 13) {
        errors.push(`Expected at least 13 columns, found ${headers.length}`);
      }
      
      // Check for critical columns
      const criticalColumns = ['Date', 'Customer Name', 'Customer Email'];
      criticalColumns.forEach((col, index) => {
        if (!headers[index] || !headers[index].toString().toLowerCase().includes(col.toLowerCase())) {
          errors.push(`Column ${String.fromCharCode(65 + index)} should be "${col}" but found "${headers[index] || 'empty'}"`);
        }
      });
      
      return {
        isValid: errors.length === 0,
        errors,
        headers
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Connection error: ${error instanceof Error ? error.message : 'Unknown error'}`],
        headers: []
      };
    }
  }
} 